/**
 * Proof pipeline — pure port of PhoneAppDetailView.submitProof (≈L696–903) and of the
 * EpisodeProgress proof mutations in EpisodeModels.swift (recordSubmittedProof,
 * stageProofDelivery, finishProofDelivery, recordRejectedProof ≈L594–762).
 * Side effects (analytics, navigation, persistence) live in the session.
 */
import type { EpisodeContent, EpisodeRequest, PhoneItemContent, ReactionSet } from './episode';
import { wrongProofResponses } from './episode';
import {
  cloneProgress,
  completeCurrentRequest,
  currentRequest,
  rejectedProofAttempts,
  type EpisodeProgress,
  type RejectedProofAttempt,
  type RejectedProofReplyReference,
} from './progress';

export type ClientChatOpenMode = 'normal' | 'animateLatestCorrectProofExchange' | 'animateLatestRejectedProofExchange';
export type ShareProofOutcome = 'correct' | 'staged' | 'close' | 'wrong' | 'textAnswerReminder' | 'noActiveRequest';

/** Data for the phone's proof feedback sheet (PhoneProofFeedback in Swift). Strings are already localized. */
export interface ProofFeedback {
  title: string;
  message: string;
  detail?: string;
  showsChatAction: boolean;
  /** 'sparkle.magnifyingglass' for the text-answer reminder. */
  primaryActionSystemImage?: string;
}

export interface ShareProofResult {
  progress: EpisodeProgress;
  outcome: ShareProofOutcome;
  /** The request the proof was checked against (undefined when there is no active request). */
  request?: EpisodeRequest;
  /** Request completed by this proof (correct without proofDelivery). */
  completedRequest?: EpisodeRequest;
  attempt?: RejectedProofAttempt;
  /**
   * Chat open mode. Correct/staged: open the chat right away with it.
   * Rejected: use it only when the player taps "View chat" on the feedback sheet.
   */
  openMode: ClientChatOpenMode;
  /** Present for rejected outcomes and noActiveRequest (iOS shows a sheet instead of opening the chat). */
  feedback?: ProofFeedback;
}

type T = (key: string) => string;
const identity: T = (k) => k;

/** Port of PhoneItemContent.proofDisplayTitle (PhoneAppFakeData.swift). */
export function proofDisplayTitle(item: PhoneItemContent): string {
  const proofTitle = item.metadata?.proofTitle?.trim();
  if (proofTitle) return proofTitle;
  const proofSubtitle = item.metadata?.proofSubtitle?.trim();
  if (proofSubtitle) return `${item.title}: ${proofSubtitle}`;
  return item.subtitle ? `${item.title}: ${item.subtitle}` : item.title;
}

/** Port of EpisodeContent.phoneItem(id:). */
export function phoneItem(episode: EpisodeContent, itemID: string): PhoneItemContent | undefined {
  for (const app of episode.phoneApps) for (const s of app.sections) for (const i of s.items) if (i.id === itemID) return i;
  return undefined;
}

/** Port of ReactionSet.closeMessage(forProofID:). */
export function closeMessage(reactions: ReactionSet, proofID: string | undefined): string {
  const override = proofID ? reactions.closeMessagesByProofID?.[proofID]?.trim() : undefined;
  return override ? override : reactions.close;
}

export function textAnswerReminderResponse(request: EpisodeRequest, t: T = identity): string {
  return request.id === 'r4_belmont_location'
    ? t("I can't use proof for this one. Type the place in chat.")
    : t("I can't use proof for this one. Type the answer in chat.");
}

export function isProofDeliveryStaged(p: EpisodeProgress, request: EpisodeRequest): boolean {
  return p.stagedProofDeliveryRequestIDs?.has(request.id) ?? false;
}

export function recordSubmittedProof(p: EpisodeProgress, title: string, itemID: string | undefined, request: EpisodeRequest): EpisodeProgress {
  const next = cloneProgress(p);
  next.submittedProofTitlesByRequestID[request.id] = title;
  if (itemID != null) next.submittedProofItemIDsByRequestID = { ...next.submittedProofItemIDsByRequestID, [request.id]: itemID };
  return next;
}

export function stageProofDelivery(p: EpisodeProgress, request: EpisodeRequest): EpisodeProgress {
  const next = cloneProgress(p);
  next.stagedProofDeliveryRequestIDs = new Set(next.stagedProofDeliveryRequestIDs ?? []);
  next.stagedProofDeliveryRequestIDs.add(request.id);
  return next;
}

export function finishProofDelivery(p: EpisodeProgress, request: EpisodeRequest): EpisodeProgress {
  if (!p.stagedProofDeliveryRequestIDs?.has(request.id)) return p;
  const next = cloneProgress(p);
  next.stagedProofDeliveryRequestIDs!.delete(request.id);
  return next;
}

export function recordSubmittedAnswer(p: EpisodeProgress, answer: string, request: EpisodeRequest): EpisodeProgress {
  const next = cloneProgress(p);
  next.submittedAnswersByRequestID[request.id] = answer;
  return next;
}

/** Port of recordRejectedProof: one attempt per (request, item), a re-send replaces the earlier one in place. */
export function recordRejectedProof(
  p: EpisodeProgress,
  input: { title: string; itemID: string; response: string; replyReference?: RejectedProofReplyReference | null },
  request: EpisodeRequest,
): { progress: EpisodeProgress; attempt: RejectedProofAttempt } {
  const attempt: RejectedProofAttempt = {
    id: `${request.id}-rejected-${input.itemID}`,
    requestID: request.id,
    itemID: input.itemID,
    title: input.title,
    response: input.response,
    replyReference: input.replyReference ?? undefined,
  };
  const next = cloneProgress(p);
  const byRequest = next.rejectedProofAttemptsByRequestID ?? {};
  const attempts = [...(byRequest[request.id] ?? [])];
  const existing = attempts.findIndex((a) => a.itemID === input.itemID);
  if (existing >= 0) attempts[existing] = attempt;
  else attempts.push(attempt);
  next.rejectedProofAttemptsByRequestID = { ...byRequest, [request.id]: attempts };
  return { progress: next, attempt };
}

function isCloseRejectedProof(attempt: RejectedProofAttempt, request: EpisodeRequest, episode: EpisodeContent): boolean {
  if (attempt.replyReference?.kind === 'close') return true;
  const proofID = phoneItem(episode, attempt.itemID)?.proofID;
  return proofID != null && (request.validation.closeProofIDs?.includes(proofID) ?? false);
}

/** Port of EpisodeProgress.rejectedProofReplyReference (legacy saves without a reference). */
export function rejectedProofReplyReference(p: EpisodeProgress, attempt: RejectedProofAttempt, request: EpisodeRequest, episode: EpisodeContent): RejectedProofReplyReference {
  if (attempt.replyReference) return attempt.replyReference;
  if (request.answerType !== 'proof') return { kind: 'textAnswerReminder' };
  if (isCloseRejectedProof(attempt, request, episode)) return { kind: 'close' };
  const responses = wrongProofResponses(request.reactions);
  const matching = responses.indexOf(attempt.response);
  if (matching >= 0) return { kind: 'wrong', variantIndex: matching };
  let ordinal = 0;
  for (const previous of rejectedProofAttempts(p, request)) {
    if (previous.id === attempt.id) break;
    if (!isCloseRejectedProof(previous, request, episode)) ordinal += 1;
  }
  return { kind: 'wrong', variantIndex: ordinal % Math.max(responses.length, 1) };
}

/** Port of RejectedProofReplyReference.resolvedText — the reply to show in the current language. */
export function resolvedRejectedProofResponse(p: EpisodeProgress, attempt: RejectedProofAttempt, request: EpisodeRequest, episode: EpisodeContent, t: T = identity): string {
  const reference = rejectedProofReplyReference(p, attempt, request, episode);
  switch (reference.kind) {
    case 'close':
      return closeMessage(request.reactions, phoneItem(episode, attempt.itemID)?.proofID);
    case 'wrong': {
      const responses = wrongProofResponses(request.reactions);
      if (responses.length === 0) return request.reactions.wrong;
      return responses[Math.max(reference.variantIndex ?? 0, 0) % responses.length];
    }
    case 'textAnswerReminder':
      return textAnswerReminderResponse(request, t);
  }
}

/** Port of migrateRejectedProofReplyReferences: gives Build 4 attempts a stable semantic reference. Run on load. */
export function migrateRejectedProofReplyReferences(p: EpisodeProgress, episode: EpisodeContent): EpisodeProgress {
  const byRequest = p.rejectedProofAttemptsByRequestID;
  if (!byRequest) return p;
  let changed = false;
  const migrated: Record<string, RejectedProofAttempt[]> = {};
  for (const [requestID, attempts] of Object.entries(byRequest)) {
    const request = episode.requests.find((r) => r.id === requestID);
    if (!request) { migrated[requestID] = attempts; continue; }
    migrated[requestID] = attempts.map((a) => {
      if (a.replyReference) return a;
      changed = true;
      return { ...a, replyReference: rejectedProofReplyReference(p, a, request, episode) };
    });
  }
  if (!changed) return p;
  const next = cloneProgress(p);
  next.rejectedProofAttemptsByRequestID = migrated;
  return next;
}

function wrongProofResponse(p: EpisodeProgress, request: EpisodeRequest, item: PhoneItemContent, episode: EpisodeContent, t: T): { text: string; reference: RejectedProofReplyReference } {
  const previous = rejectedProofAttempts(p, request);
  const existing = previous.find((a) => a.itemID === item.id);
  if (existing) {
    return { text: resolvedRejectedProofResponse(p, existing, request, episode, t), reference: rejectedProofReplyReference(p, existing, request, episode) };
  }
  const responses = wrongProofResponses(request.reactions);
  const used = new Set(
    previous
      .map((a) => rejectedProofReplyReference(p, a, request, episode))
      .filter((r) => r.kind === 'wrong')
      .map((r) => r.variantIndex ?? 0),
  );
  const fresh = responses.findIndex((_, i) => !used.has(i));
  const index = fresh >= 0 ? fresh : previous.length % responses.length;
  return { text: responses[index], reference: { kind: 'wrong', variantIndex: index } };
}

/**
 * "Share proof with {client}". `t` localizes the iOS L() strings (reminders, sheet titles).
 * Every share records `sentProofIDs` (iOS inserts before checking the request type).
 */
export function shareProof(progress: EpisodeProgress, episode: EpisodeContent, request: EpisodeRequest | undefined, item: PhoneItemContent, t: T = identity): ShareProofResult {
  const active = request ?? currentRequest(progress, episode);
  if (progress.solved || !active) {
    return {
      progress,
      outcome: 'noActiveRequest',
      openMode: 'normal',
      feedback: { title: t('No active request'), message: t("There isn't a current question for this proof yet."), showsChatAction: false },
    };
  }
  let next = progress;
  if (item.proofID) {
    next = cloneProgress(next);
    next.sentProofIDs.add(item.proofID);
  }
  const title = proofDisplayTitle(item);
  const proofID = item.proofID;
  const isClose = proofID != null && (active.validation.closeProofIDs?.includes(proofID) ?? false);

  if (active.answerType !== 'proof') {
    const response = isClose ? closeMessage(active.reactions, proofID) : textAnswerReminderResponse(active, t);
    const recorded = recordRejectedProof(next, { title, itemID: item.id, response, replyReference: isClose ? { kind: 'close' } : { kind: 'textAnswerReminder' } }, active);
    return {
      progress: recorded.progress,
      outcome: isClose ? 'close' : 'textAnswerReminder',
      request: active,
      attempt: recorded.attempt,
      openMode: 'animateLatestRejectedProofExchange',
      feedback: {
        title: isClose ? 'Close' : active.id === 'r4_belmont_location' ? t('Type the place') : t('Type the answer'),
        message: response,
        detail: 'Written answer',
        showsChatAction: true,
        primaryActionSystemImage: 'sparkle.magnifyingglass',
      },
    };
  }

  if (proofID != null && active.validation.correctProofIDs.includes(proofID)) {
    next = recordSubmittedProof(next, title, item.id, active);
    if (active.proofDelivery) {
      return { progress: stageProofDelivery(next, active), outcome: 'staged', request: active, openMode: 'animateLatestCorrectProofExchange' };
    }
    const completed = completeCurrentRequest(next, episode);
    return { progress: completed.progress, outcome: 'correct', request: active, completedRequest: completed.request, openMode: 'animateLatestCorrectProofExchange' };
  }

  const detail = active.proofDelivery == null ? t('Saved to chat') : t('Keep looking here');
  const showsChatAction = active.proofDelivery == null;
  if (isClose) {
    const message = closeMessage(active.reactions, proofID);
    const recorded = recordRejectedProof(next, { title, itemID: item.id, response: message, replyReference: { kind: 'close' } }, active);
    return { progress: recorded.progress, outcome: 'close', request: active, attempt: recorded.attempt, openMode: 'animateLatestRejectedProofExchange', feedback: { title: 'Close', message, detail, showsChatAction } };
  }
  const reply = wrongProofResponse(next, active, item, episode, t);
  const recorded = recordRejectedProof(next, { title, itemID: item.id, response: reply.text, replyReference: reply.reference }, active);
  return { progress: recorded.progress, outcome: 'wrong', request: active, attempt: recorded.attempt, openMode: 'animateLatestRejectedProofExchange', feedback: { title: t('Not it'), message: reply.text, detail, showsChatAction } };
}

/** The staged proof waiting to be attached in a conversation (port of pendingConversationProof, data part). */
export function pendingStagedProof(progress: EpisodeProgress, episode: EpisodeContent): { request: EpisodeRequest; item?: PhoneItemContent } | undefined {
  const request = currentRequest(progress, episode);
  if (!request?.proofDelivery || !isProofDeliveryStaged(progress, request)) return undefined;
  const itemID = progress.submittedProofItemIDsByRequestID?.[request.id];
  return { request, item: itemID ? phoneItem(episode, itemID) : undefined };
}

/**
 * Whether a Messages conversation can receive the staged proof: iOS matches the
 * conversation title against `proofDelivery.recipientName` (case-insensitive); the
 * delivery's own `conversationID` (the thread shown after delivery) is accepted too.
 */
export function acceptsStagedProof(episode: EpisodeContent, request: EpisodeRequest, conversationID: string): boolean {
  const delivery = request.proofDelivery;
  if (!delivery) return false;
  if (conversationID === delivery.conversationID) return true;
  const conversation = phoneItem(episode, conversationID);
  return conversation != null && conversation.title.toLowerCase().includes(delivery.recipientName.toLowerCase());
}

/**
 * Messages app: attach the staged proof (port of deliverPendingConversationProof).
 * Completes the request and clears the staging. When `conversationID` is given it must
 * pass `acceptsStagedProof`. After delivery iOS opens `request.proofDelivery.conversationID`.
 */
export function deliverStagedProof(progress: EpisodeProgress, episode: EpisodeContent, request?: EpisodeRequest, conversationID?: string): { progress: EpisodeProgress; completedRequest?: EpisodeRequest } {
  const active = currentRequest(progress, episode);
  const target = request ?? active;
  if (!target || !active || active.id !== target.id || !target.proofDelivery || !isProofDeliveryStaged(progress, target)) return { progress };
  if (conversationID != null && !acceptsStagedProof(episode, target, conversationID)) return { progress };
  const completed = completeCurrentRequest(finishProofDelivery(progress, target), episode);
  return { progress: completed.progress, completedRequest: completed.request };
}
